/**
 * Heading with Anchor Component
 * 
 * Renders a heading (h2 or h3) with a clickable anchor link icon.
 * When clicked, updates the browser URL and copies the full URL to clipboard.
 */

"use client";

import { useState } from "react";
import { LinkIcon, CheckIcon } from "@heroicons/react/24/outline";

interface HeadingWithAnchorProps {
  level: "h2" | "h3";
  children: string;
  slug: string;
}

export default function HeadingWithAnchor({ level, children, slug }: HeadingWithAnchorProps) {
  const [copied, setCopied] = useState(false);

  const handleAnchorClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Get the full URL with anchor
    const fullUrl = `${window.location.origin}${window.location.pathname}#${slug}`;
    
    // Update browser URL without reload
    window.history.pushState(null, '', `#${slug}`);
    
    // Copy to clipboard
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  const headingClasses = level === "h2" 
    ? "font-poppins text-3xl font-bold text-gray-900 my-8 scroll-mt-24" 
    : "font-poppins text-2xl font-semibold text-gray-900 my-6 scroll-mt-24";

  const Heading = level;

  return (
    <Heading id={slug} className={`${headingClasses} group flex items-center gap-3`}>
      {children}
      <button
        onClick={handleAnchorClick}
        className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
        aria-label="Copy link to section"
      >
        {copied ? (
          <CheckIcon className="h-5 w-5 text-green-600" />
        ) : (
          <LinkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
        )}
      </button>
    </Heading>
  );
}

